import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import NetworkError from 'shared/exceptions/network';
import { initialState } from './initial';
import { setAuthUserData } from 'shared/helpers/auth';
import {
  fetchLogin,
  fetchLoginMax,
  fetchLoginWeb,
  fetchRegister,
  fetchRegisterMax,
  fetchRegisterWeb,
  fetchSendSms,
} from './services/loginServices';

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    setAuth(state, action: PayloadAction<boolean>) {
      state.auth.isAuth = action.payload;
    },
    setRefferal(state, action: PayloadAction<string | undefined>) {
      state.refferal = action.payload;
    },
    setPollId(state, action: PayloadAction<string | undefined>) {
      state.poll_id = action.payload;
    },
    setRegPhone(state, action: PayloadAction<string>) {
      state.register.phone = action.payload;
    },
    setRegName(state, action: PayloadAction<string>) {
      state.register.name = action.payload;
    },
    setRegSurname(state, action: PayloadAction<string>) {
      state.register.surname = action.payload;
    },
    setRegPatronymic(state, action: PayloadAction<string>) {
      state.register.patronymic = action.payload;
    },
    setRegCode(state, action: PayloadAction<string>) {
      state.register.code = action.payload;
    },
    setRegGender(state, action: PayloadAction<string>) {
      state.register.gender = action.payload;
    },
    setRegBirth(state, action: PayloadAction<Date | undefined>) {
      state.register.birth = action.payload;
    },
    setRegEducation(state, action: PayloadAction<string>) {
      state.register.education = action.payload;
    },
    setRegAgreement(state, action: PayloadAction<boolean>) {
      state.register.agreement = action.payload;
    },
    setRegPrivacy(state, action: PayloadAction<boolean>) {
      state.register.privacy = action.payload;
    },
    setRegOwnUse(state, action: PayloadAction<boolean>) {
      state.register.own_respondents_use = action.payload;
    },
    setRegSmartCaptureToken(state, action: PayloadAction<string>) {
      state.register.smartCaptureToken = action.payload;
    },
    setChangedDataValue(
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) {
      const { name, value } = action.payload;
      state.register.birth = value;
    },
  },

  extraReducers: builder => {
    //POST
    //login/fetchLogin
    builder.addCase(fetchLogin.pending, state => {
      state.auth.loading = true;
      state.auth.error = undefined;
    });

    builder.addCase(fetchLogin.fulfilled, (state, { payload, meta }) => {
      state.auth.loading = false;
      state.auth.error = undefined;
      if (payload.data.data.token) {
        state.auth.isAuth = true;
        state.auth.isReenteringLink =
          !!payload.data.data.own_respondent_hash_accepted;
        setAuthUserData(payload.data.data.token);
        if (meta.arg?.initData?.user?.id) {
          state.auth.initDataId = meta.arg.initData.user?.id;
        }
      }
    });

    builder.addCase(fetchLogin.rejected, (state, action) => {
      state.auth.loading = false;
      const message = (action.payload as NetworkError).message;
      state.auth.error = { ...(action.payload as NetworkError), message };
    });

    //login/fetchLoginMax
    builder.addCase(fetchLoginMax.pending, state => {
      state.auth.loading = true;
      state.auth.error = undefined;
    });

    builder.addCase(fetchLoginMax.fulfilled, (state, { payload }) => {
      state.auth.loading = false;
      state.auth.error = undefined;
      if (payload.data.data.token) {
        state.auth.isAuth = true;
        state.auth.isReenteringLink =
          !!payload.data.data.own_respondent_hash_accepted;
        setAuthUserData(payload.data.data.token);
      }
    });

    builder.addCase(fetchLoginMax.rejected, (state, action) => {
      state.auth.loading = false;
      const message = (action.payload as NetworkError).message;
      state.auth.error = { ...(action.payload as NetworkError), message };
    });

    //login/fetchLoginWeb (авторизация через WEB)
    builder.addCase(fetchLoginWeb.pending, state => {
      state.auth.loading = true;
      state.auth.error = undefined;
    });

    builder.addCase(fetchLoginWeb.fulfilled, (state, { payload }) => {
      state.auth.loading = false;
      state.auth.error = undefined;
      if (payload.data.data.token) {
        state.auth.isAuth = true;
        state.auth.isReenteringLink =
          !!payload.data.data.own_respondent_hash_accepted;
        setAuthUserData(payload.data.data.token);
      }
    });

    builder.addCase(fetchLoginWeb.rejected, (state, action) => {
      state.auth.loading = false;
      const message = (action.payload as NetworkError).message;
      state.auth.error = { ...(action.payload as NetworkError), message };
    });

    //login/fetchRegister
    builder.addCase(fetchRegister.pending, state => {
      state.register.loading = true;
      state.register.error = undefined;
    });

    builder.addCase(fetchRegister.fulfilled, (state, { payload, meta }) => {
      state.register.loading = false;
      state.register.error = undefined;
      if (payload.data.data.token) {
        state.auth.error = undefined;
        state.auth.isAuth = true;
        setAuthUserData(payload.data.data.token);
        if (meta.arg?.initData?.user?.id) {
          state.auth.initDataId = meta.arg.initData.user?.id;
        }
        if (meta.arg.own_respondents_link_hash) {
          state.auth.isRegUsingLink = true;
        }
      }
    });

    builder.addCase(fetchRegister.rejected, (state, action) => {
      state.register.loading = false;
      const message = (action.payload as NetworkError).message;
      state.register.error = { ...(action.payload as NetworkError), message };
    });

    //login/fetchRegisterMax
    builder.addCase(fetchRegisterMax.pending, state => {
      state.register.loading = true;
      state.register.error = undefined;
    });

    builder.addCase(fetchRegisterMax.fulfilled, (state, { payload, meta }) => {
      state.register.loading = false;
      state.register.error = undefined;
      if (payload.data.data.token) {
        state.auth.error = undefined;
        state.auth.isAuth = true;
        setAuthUserData(payload.data.data.token);
        // Note: For MAX, we don't have initData.user.id in the same structure, but you might want to add logic here if needed
        if (meta.arg.own_respondents_link_hash) {
          state.auth.isRegUsingLink = true;
        }
      }
    });

    builder.addCase(fetchRegisterMax.rejected, (state, action) => {
      state.register.loading = false;
      const message = (action.payload as NetworkError).message;
      state.register.error = { ...(action.payload as NetworkError), message };
    });

    //login/fetchRegisterWeb
    builder.addCase(fetchRegisterWeb.pending, state => {
      state.register.loading = true;
      state.register.error = undefined;
    });

    builder.addCase(fetchRegisterWeb.fulfilled, (state, { payload, meta }) => {
      state.register.loading = false;
      state.register.error = undefined;
      if (payload.data.data.token) {
        state.auth.error = undefined;
        state.auth.isAuth = true;
        setAuthUserData(payload.data.data.token);
        if (meta.arg.own_respondents_link_hash) {
          state.auth.isRegUsingLink = true;
        }
      }
    });

    builder.addCase(fetchRegisterWeb.rejected, (state, action) => {
      state.register.loading = false;
      const message = (action.payload as NetworkError).message;
      state.register.error = { ...(action.payload as NetworkError), message };
    });

    //login/fetchSendSms
    builder.addCase(fetchSendSms.pending, state => {
      state.sms.loading = true;
      state.sms.error = undefined;
      state.register.error = undefined;
      state.register.code = '';
    });

    builder.addCase(fetchSendSms.fulfilled, (state, { payload }) => {
      state.sms.loading = false;
      state.sms.error = undefined;
      state.sms.message = payload.data.data.message;
    });

    builder.addCase(fetchSendSms.rejected, (state, action) => {
      state.sms.loading = false;
      const message = (action.payload as NetworkError).message;
      state.sms.error = { ...(action.payload as NetworkError), message };
    });
  },
});

export const { actions: loginActions } = loginSlice;
export const { reducer: loginReducer } = loginSlice;
